import { Api } from '@monorepo/shared/apiClient';
import {
  DocumentFoldersCacheEvents,
  documentFoldersCacheRegister,
} from '@monorepo/shared/cacheRegisters/documents/documentFoldersCacheRegister';
import {
  OrganizationDocumentsCacheEvents,
  organizationDocumentsCacheRegister,
} from '@monorepo/shared/cacheRegisters/documents/organizationDocumentsCacheRegister';
import {
  SiteDocumentsCacheEvents,
  siteDocumentsCacheRegister,
} from '@monorepo/shared/cacheRegisters/documents/siteDocumentsCacheRegister';
import {
  getKeyParamsForInvalidation,
  getQueryConfig,
} from '@monorepo/shared/utils/queryUtils';
import { ProjectDocumentsListResponse } from 'mapistry-shared';
import { queryCache, QueryConfig, useQuery } from 'react-query';

type UseSiteDocumentsListParams = Partial<Api.FetchProjectDocumentsParams> & {
  config?: QueryConfig<ProjectDocumentsListResponse, Api.ErrorResponse>;
};

const KEY_START = 'site-documents-list';

export const createKey = ({
  organizationId,
  projectId,
}: Api.FetchProjectDocumentsParams) =>
  [KEY_START, organizationId, projectId] as const;

const fetcher: Api.DataHookQueryFn<
  typeof createKey,
  typeof Api.fetchProjectDocuments
> = (_key, organizationId, projectId) =>
  Api.fetchProjectDocuments({ organizationId, projectId });

export function useSiteDocumentsList({
  organizationId,
  projectId,
  config: inputConfig,
}: UseSiteDocumentsListParams) {
  const isEnabled = !!organizationId && !!projectId;
  const config = getQueryConfig(inputConfig, isEnabled);
  const key = isEnabled ? createKey({ organizationId, projectId }) : undefined;
  const { data, ...queryInfo } = useQuery(key, fetcher, config);
  return { ...queryInfo, siteDocuments: data };
}

type InvalidateCacheParams = {
  organizationId: string;
  projectId?: string;
};

export async function invalidateCache(
  keyParams: InvalidateCacheParams,
): Promise<void> {
  await queryCache.invalidateQueries(
    getKeyParamsForInvalidation([
      KEY_START,
      keyParams.organizationId,
      keyParams.projectId,
    ]),
  );
}

siteDocumentsCacheRegister(
  [
    SiteDocumentsCacheEvents.CREATE,
    SiteDocumentsCacheEvents.UPDATE,
    SiteDocumentsCacheEvents.DELETE,
    SiteDocumentsCacheEvents.MOVE,
  ],
  {
    hookName: 'useSiteDocumentsList',
    callback: (keyParams) => invalidateCache(keyParams),
  },
);

// Folder create/rename/delete in Setup changes what this page lists.
documentFoldersCacheRegister(
  [
    DocumentFoldersCacheEvents.CREATE,
    DocumentFoldersCacheEvents.UPDATE,
    DocumentFoldersCacheEvents.DELETE,
  ],
  {
    hookName: 'useSiteDocumentsList',
    callback: (keyParams) => invalidateCache(keyParams),
  },
);

// Org-scoped folder contents and folder fileCounts show up on this page too.
organizationDocumentsCacheRegister(
  [
    OrganizationDocumentsCacheEvents.CREATE,
    OrganizationDocumentsCacheEvents.UPDATE,
    OrganizationDocumentsCacheEvents.DELETE,
    OrganizationDocumentsCacheEvents.MOVE,
  ],
  {
    hookName: 'useSiteDocumentsList',
    callback: (keyParams) => invalidateCache(keyParams),
  },
);
